import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import { getSubscriptionTypesPublic } from "../services/authService";
import playstore from "../images/playstore.png";
import appstore from "../images/appstore.svg";
import screenSportifSeances from "../images/screen-sportif-seances.jpg";
import coachStudentCard from "../images/coach-student-card.png";

function FadeInSection({ children, className = "" }) {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setVisible(true);
					observer.unobserve(entry.target);
				}
			},
			{ threshold: 0.15 }
		);
		if (ref.current) observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			ref={ref}
			className={`gnv3-fade ${visible ? "visible" : ""} ${className}`}
		>
			{children}
		</div>
	);
}

const FALLBACK_PLANS = {
	starter: ["Entraînements illimités", "Synchronisation cloud", "Templates personnalisés", "Dashboard mobile", "Jusqu'à 10 élèves", "Planification avancée"],
	pro: ["Entraînements illimités", "Synchronisation cloud", "Templates personnalisés", "Dashboard mobile", "Planification avancée", "Dashboard Coach web", "Jusqu'à 100 élèves"],
};

const FAQ_ITEMS = [
	{
		q: "L'app fonctionne-t-elle sans connexion ?",
		a: "Oui, GymNote est 100% offline-first. Toutes tes données sont stockées localement sur ton téléphone. La synchronisation cloud se fait automatiquement dès que tu retrouves une connexion.",
	},
	{
		q: "Puis-je suivre plusieurs élèves ?",
		a: "Oui, avec un abonnement Coach (Starter ou Pro). Le plan Starter te permet de suivre jusqu'à 10 élèves, le plan Pro jusqu'à 100. Chaque élève accepte ton invitation depuis son app.",
	},
	{
		q: "Comment fonctionne la synchronisation ?",
		a: "La synchronisation est automatique : elle se déclenche toutes les 10 secondes quand tu es connecté. En cas de conflit, GymNote conserve toujours la version la plus récente.",
	},
	{
		q: "Est-ce que mes données sont sauvegardées ?",
		a: "Absolument. Tes entraînements, mesures et exercices sont sauvegardés en temps réel sur nos serveurs sécurisés. Tu peux aussi exporter tes données à tout moment depuis les paramètres.",
	},
	{
		q: "Puis-je annuler mon abonnement à tout moment ?",
		a: "Oui, sans engagement. Tu peux annuler ton abonnement depuis ton dashboard directement dans l'app. La résiliation prend effet à la fin de la période en cours.",
	},
	{
		q: "L'app est-elle disponible sur iOS et Android ?",
		a: "GymNote est disponible sur les deux plateformes : télécharge-la sur l'App Store (iPhone/iPad) ou sur Google Play (Android). L'expérience est identique sur les deux.",
	},
	{
		q: "Comment inviter un élève ?",
		a: "Depuis l'app, rends-toi dans ton profil puis dans la section Partenaires. Génère un code d'invitation unique et transmets-le à ton élève. Une fois le code utilisé de son côté, tu as accès à ses séances.",
	},
	{
		q: "Y a-t-il une période d'essai ?",
		a: "Oui, tu bénéficies de 30 jours gratuits pour tester le mode Coach sans aucune restriction. Aucune carte bancaire requise pour démarrer l'essai.",
	},
];

const FEATURES = [
	{
		icon: "mdi-clipboard-text-clock-outline",
		title: "Saisie éclair",
		text: "Reprends tes charges précédentes en un tap. Séries, reps et RPE en quelques secondes.",
	},
	{
		icon: "mdi-chart-line",
		title: "Courbes de force",
		text: "Visualise l'évolution de chaque exercice, ton volume hebdo et tes 1RM estimés.",
	},
	{
		icon: "mdi-dumbbell",
		title: "Programmes",
		text: "Crée tes splits ou suis un programme. Push/Pull/Legs, full-body, force… tout est modulable.",
	},
	{
		icon: "mdi-trophy-outline",
		title: "Records & badges",
		text: "Chaque PR est détecté automatiquement et fêté. Garde ta motivation au sommet.",
		variant: "orange",
	},
	{
		icon: "mdi-timer-outline",
		title: "Minuteur de repos",
		text: "Chrono intelligent entre les séries, avec rappels et durée adaptée à l'exercice.",
	},
	{
		icon: "mdi-account-supervisor-outline",
		title: "Lien coach",
		text: "Partage tes séances avec ton coach et reçois ses ajustements en temps réel.",
	},
];

const TRUST_ITEMS = [
	{ icon: "mdi-cloud-sync", label: "Sync multi-appareils" },
	{ icon: "mdi-wifi-off", label: "Mode hors-ligne" },
	{ icon: "mdi-cancel", label: "Sans pub" },
	{ icon: "mdi-chart-line", label: "Stats détaillées" },
	{ icon: "mdi-account-group-outline", label: "Multi-élèves" },
];

function FaqAccordion() {
	const [openIndex, setOpenIndex] = useState(null);
	const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

	return (
		<FadeInSection>
			<section className="gnv3-sec gnv3-faq" id="faq">
				<div className="gnv3-wrap">
					<div className="gnv3-sec-head center">
						<span className="gnv3-eyebrow">FAQ</span>
						<h2 className="gnv3-h2">Tes questions, nos réponses.</h2>
						<p className="gnv3-sub">Tout ce que tu veux savoir avant de te lancer.</p>
					</div>
					<div className="gnv3-faq-list">
						{FAQ_ITEMS.map((item, i) => (
							<div
								key={i}
								className={`gnv3-faq-item${openIndex === i ? " open" : ""}`}
							>
								<button
									className="gnv3-faq-q"
									onClick={() => toggle(i)}
									aria-expanded={openIndex === i}
								>
									<span>{item.q}</span>
									<span className="gnv3-faq-icon" aria-hidden="true">+</span>
								</button>
								<div className="gnv3-faq-a">
									<p>{item.a}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		</FadeInSection>
	);
}

function Home() {
	const [billingPeriod, setBillingPeriod] = useState("monthly");
	const [plans, setPlans] = useState(null);

	useEffect(() => {
		const fetchPlans = async () => {
			const result = await getSubscriptionTypesPublic();
			if (result.success) {
				setPlans(result.types);
			}
		};
		fetchPlans();
	}, []);

	const starterPlans = plans?.filter(p => p.libelle?.toLowerCase().includes("starter") && p.prix > 0) || [];
	const proPlans = plans?.filter(p => p.libelle?.toLowerCase().includes("pro") && p.prix > 0) || [];
	const starterMonthly = starterPlans.find(p => p.dureeMois === 1);
	const starterYearly = starterPlans.find(p => p.dureeMois === 12);
	const proMonthly = proPlans.find(p => p.dureeMois === 1);
	const proYearly = proPlans.find(p => p.dureeMois === 12);

	const starterFeatures = (starterMonthly || starterYearly)?.fonctionnalites?.map(f => f.libelle) || FALLBACK_PLANS.starter;
	const proFeatures = (proMonthly || proYearly)?.fonctionnalites?.map(f => f.libelle) || FALLBACK_PLANS.pro;

	const starterPlan = billingPeriod === "yearly" && starterYearly ? starterYearly : starterMonthly;
	const starterPrice = starterPlan?.prix ?? (billingPeriod === "monthly" ? 9.99 : 99.99);
	const proPlan = billingPeriod === "yearly" && proYearly ? proYearly : proMonthly;
	const proPrice = proPlan?.prix ?? (billingPeriod === "monthly" ? 19.99 : 199.99);
	const starterIsYearly = starterPlan?.dureeMois === 12;
	const proIsYearly = proPlan?.dureeMois === 12;

	return (
		<div className="gnv3-page">
			{/* ============ HERO ============ */}
			<section className="gnv3-hero">
				<div className="gnv3-wrap gnv3-hero-grid">
					<div className="gnv3-hero-copy">
						<span className="gnv3-eyebrow">Plateforme coaching</span>
						<h1 className="gnv3-h1">
							Chaque élève compte.<br />
							<span className="gnv3-accent">Aucun ne décroche.</span>
						</h1>
						<p className="gnv3-lead">
							GymNote est le carnet d'entraînement utilisé par tes élèves au quotidien,
							et le dashboard qui te fait piloter leur progression sans y passer
							tes dimanches.
						</p>
						<div className="gnv3-hero-actions">
							<a
								className="gnv3-store-link"
								href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
								target="_blank"
								rel="noopener noreferrer"
							>
								<img src={appstore} alt="Télécharger dans l'App Store" />
							</a>
							<a
								className="gnv3-store-link"
								href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
								target="_blank"
								rel="noopener noreferrer"
							>
								<img src={playstore} alt="Disponible sur Google Play" />
							</a>
						</div>
						<div className="gnv3-store-note">
							<span className="mdi mdi-star"></span>
							5/5 sur App Store & Play Store · plus de 20 000 performances enregistrées
						</div>
						<div className="gnv3-hero-stats">
							<div>
								<div className="gnv3-stat-num">30 jours</div>
								<div className="gnv3-stat-lbl">d'essai coach offerts</div>
							</div>
							<div>
								<div className="gnv3-stat-num">2 min</div>
								<div className="gnv3-stat-lbl">pour saisir une séance</div>
							</div>
						</div>
					</div>

					<div className="gnv3-hero-visual">
						<div className="gnv3-glow gnv3-glow-teal"></div>
						<div className="gnv3-glow gnv3-glow-deep"></div>
						<div className="gnv3-deco-bar" style={{ width: "170px", top: "30px", left: 0, transform: "rotate(-8deg)" }}></div>
						<div className="gnv3-deco-bar" style={{ width: "120px", bottom: "50px", right: "10px", transform: "rotate(6deg)" }}></div>

						<div className="gnv3-phone">
							<div className="gnv3-phone-screen">
								<img src={screenSportifSeances} alt="App GymNote (accueil)" />
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* ============ TRUST ============ */}
			<section className="gnv3-trust">
				<div className="gnv3-wrap gnv3-trust-inner">
					{TRUST_ITEMS.map((it, i) => (
						<div className="gnv3-trust-it" key={i}>
							<span className={`mdi ${it.icon}`}></span>
							{it.label}
						</div>
					))}
				</div>
			</section>

			{/* ============ FEATURES ============ */}
			<FadeInSection>
				<section className="gnv3-sec" id="features">
					<div className="gnv3-wrap">
						<div className="gnv3-sec-head">
							<span className="gnv3-eyebrow">Conçu pour progresser</span>
							<h2 className="gnv3-h2">Tout ton entraînement, structuré.</h2>
							<p className="gnv3-sub">
								Saisis tes séries en quelques secondes, analyse en profondeur.
								GymNote transforme chaque séance en levier de progression et te
								montre exactement où pousser pour avancer.
							</p>
						</div>
						<div className="gnv3-feat-grid">
							{FEATURES.map((f, i) => (
								<article key={i} className="gnv3-feat-card">
									<div className={`gnv3-feat-ic${f.variant === "orange" ? " gnv3-feat-ic-orange" : ""}`}>
										<span className={`mdi ${f.icon}`}></span>
									</div>
									<h3 className="gnv3-feat-title">{f.title}</h3>
									<p className="gnv3-feat-text">{f.text}</p>
								</article>
							))}
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ PROGRESS (dark band) ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-sec-band" id="progress">
					<div className="gnv3-band-dark">
						<div className="gnv3-band-glow"></div>
						<div className="gnv3-prog-grid">
							<div>
								<span className="gnv3-eyebrow gnv3-eyebrow-on-dark">Analyse de progression</span>
								<h2 className="gnv3-h2 gnv3-h2-on-dark">
									Les chiffres qui prouvent que tu progresses.
								</h2>
								<p className="gnv3-sub gnv3-sub-on-dark">
									GymNote relie chaque répétition à une tendance. Tu vois ce qui marche,
									et tu ajustes avant de stagner.
								</p>
								<ul className="gnv3-band-list">
									<li>
										<span className="mdi mdi-trending-up"></span>
										<span>Évolution du 1RM estimé, exercice par exercice</span>
									</li>
									<li>
										<span className="mdi mdi-chart-bar"></span>
										<span>Volume, tonnage et fréquence par groupe musculaire</span>
									</li>
									<li>
										<span className="mdi mdi-bullseye-arrow"></span>
										<span>Objectifs personnalisés avec suivi visuel</span>
									</li>
								</ul>
							</div>
							<div className="gnv3-chart-card">
								<div className="gnv3-chart-head">
									<div>
										<div className="gnv3-chart-t">Développé couché · 1RM estimé</div>
										<div className="gnv3-chart-big">112,5 kg</div>
									</div>
									<div className="gnv3-chart-delta">
										<span className="mdi mdi-arrow-top-right"></span> +18%
									</div>
								</div>
								<svg className="gnv3-chart-svg" viewBox="0 0 480 180" preserveAspectRatio="none">
									<defs>
										<linearGradient id="gnv3-fill" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor="#30a49a" stopOpacity=".45" />
											<stop offset="100%" stopColor="#30a49a" stopOpacity="0" />
										</linearGradient>
									</defs>
									<line x1="0" y1="45" x2="480" y2="45" stroke="rgba(234,241,237,.10)" strokeWidth="1" />
									<line x1="0" y1="95" x2="480" y2="95" stroke="rgba(234,241,237,.10)" strokeWidth="1" />
									<line x1="0" y1="145" x2="480" y2="145" stroke="rgba(234,241,237,.10)" strokeWidth="1" />
									<path d="M0,150 L80,138 L160,120 L240,110 L320,78 L400,64 L480,40 L480,180 L0,180 Z" fill="url(#gnv3-fill)" />
									<path d="M0,150 L80,138 L160,120 L240,110 L320,78 L400,64 L480,40" fill="none" stroke="#30a49a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
									<circle cx="480" cy="40" r="5" fill="#30a49a" stroke="#17211E" strokeWidth="2.5" />
									<circle cx="320" cy="78" r="4" fill="#17211E" stroke="#30a49a" strokeWidth="2.5" />
								</svg>
								<div className="gnv3-chart-x">
									<span>Jan</span>
									<span>Fév</span>
									<span>Mar</span>
									<span>Avr</span>
									<span>Mai</span>
									<span>Juin</span>
								</div>
							</div>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ COACH MODE (orange) ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-coach" id="coach">
					<div className="gnv3-wrap gnv3-coach-grid">
						<div className="gnv3-coach-visual-col">
							<div className="gnv3-coach-visual">
								<img src={coachStudentCard} alt="Vue d'une fiche élève dans le mode Coach GymNote" />
							</div>
							<div className="gnv3-coach-caption">
								<span className="mdi mdi-monitor-dashboard"></span>
								Extrait du dashboard web
							</div>
						</div>
						<div>
							<span className="gnv3-pill-tag">
								<span className="mdi mdi-account-tie"></span>Espace coach
							</span>
							<span className="gnv3-eyebrow gnv3-eyebrow-orange">Un mode dédié</span>
							<h2 className="gnv3-h2">
								Pilote tous tes athlètes depuis un seul tableau de bord.
							</h2>
							<p className="gnv3-sub">
								Suis 30 élèves sans y perdre tes dimanches. Programme à l'avance,
								repère qui décroche, ajuste à distance, depuis ton téléphone
								ou ton ordinateur.
							</p>
							<ul className="gnv3-coach-points">
								<li>
									<span className="mdi mdi-eye-outline"></span>
									<span>Vois les séances de chaque athlète en temps réel</span>
								</li>
								<li>
									<span className="mdi mdi-pencil-ruler"></span>
									<span>Assigne et ajuste les programmes à distance</span>
								</li>
								<li>
									<span className="mdi mdi-message-text-outline"></span>
									<span>Commente une série, corrige une charge, encourage</span>
								</li>
								<li>
									<span className="mdi mdi-alert-circle-outline"></span>
									<span>Repère les stagnations avant qu'elles s'installent</span>
								</li>
							</ul>
							<div className="gnv3-hero-actions">
								<a
									className="gnv3-store-link"
									href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img src={appstore} alt="Télécharger dans l'App Store" />
								</a>
								<a
									className="gnv3-store-link"
									href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img src={playstore} alt="Disponible sur Google Play" />
								</a>
							</div>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ GUIDE COACH PDF ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-guide" id="guide">
					<div className="gnv3-wrap gnv3-guide-grid">
						<div className="gnv3-guide-cover-col">
							<div className="gnv3-guide-cover" aria-hidden="true">
								<span className="gnv3-guide-cover-stamp">Édition 2026</span>
								<div className="gnv3-guide-cover-brand">
									<span className="mdi mdi-notebook-edit-outline"></span>
									<span>GymNote</span>
								</div>
								<div className="gnv3-guide-cover-title">
									Guide Coach
									<span className="gnv3-guide-cover-accent">GymNote.</span>
								</div>
								<div className="gnv3-guide-cover-meta">
									<span>10 chapitres</span>
									<span className="gnv3-guide-cover-dot">·</span>
									<span>Mobile + web</span>
									<span className="gnv3-guide-cover-dot">·</span>
									<span>PDF</span>
								</div>
							</div>
						</div>
						<div className="gnv3-guide-content">
							<span className="gnv3-eyebrow">Documentation</span>
							<h2 className="gnv3-h2">
								Le guide pour activer ton mode coach,
								<span className="gnv3-accent"> en PDF.</span>
							</h2>
							<p className="gnv3-sub">
								Toutes les fonctionnalités du mode coach expliquées dans l'ordre :
								dashboard mobile, rapport hebdo, statistiques élève, éditeur de séance,
								templates, messagerie. Lis-le une fois pour t'installer, puis garde-le
								pour piocher le chapitre dont tu as besoin.
							</p>
							<ul className="gnv3-guide-points">
								<li>
									<span className="mdi mdi-image-multiple-outline"></span>
									<span>Captures d'écran annotées de chaque module clé</span>
								</li>
								<li>
									<span className="mdi mdi-cellphone-link"></span>
									<span>Les workflows mobile et web mis côte à côte</span>
								</li>
								<li>
									<span className="mdi mdi-lightbulb-on-outline"></span>
									<span>Conseils d'organisation et astuces de coachs</span>
								</li>
							</ul>
							<a
								href="/fichiers/Guide-Coach-GymNote.pdf"
								download="Guide-Coach-GymNote.pdf"
								className="gnv3-btn gnv3-btn-orange gnv3-guide-cta"
							>
								<span className="mdi mdi-download"></span>
								Télécharger le guide
							</a>
							<div className="gnv3-guide-fineprint">
								<span className="mdi mdi-information-outline"></span>
								Mis à jour en juin 2026 · PDF ~19 Mo
							</div>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ SOCIAL / COMMUNAUTÉ ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-social" id="social">
					<div className="gnv3-wrap gnv3-social-grid">
						<div className="gnv3-social-copy">
							<span className="gnv3-eyebrow">Communauté</span>
							<h2 className="gnv3-h2">
								Tes élèves ne s'entraînent <span className="gnv3-accent">jamais seuls.</span>
							</h2>
							<p className="gnv3-sub">
								Le fil d'actu de leurs GymBros, les réactions sur leurs nouveaux
								records, les commentaires sur les séries lourdes. L'app maintient
								leur motivation entre deux séances, sans que tu aies à animer
								quoi que ce soit.
							</p>
							<ul className="gnv3-social-points">
								<li>
									<span className="gnv3-social-emoji">💪</span>
									<div>
										<strong>Réactions instantanées</strong>
										<span>Force, cœur, choqué, mort de rire : tes élèves se motivent série par série.</span>
									</div>
								</li>
								<li>
									<span className="gnv3-social-emoji">💬</span>
									<div>
										<strong>Commentaires sur les perfs</strong>
										<span>Une remarque sur le nouveau PR, une question sur la technique. La conversation reste autour de la salle.</span>
									</div>
								</li>
								<li>
									<span className="gnv3-social-emoji">📡</span>
									<div>
										<strong>Fil d'actu GymBros</strong>
										<span>Chaque progression de leurs partenaires apparaît dans leur feed. Effet de groupe garanti.</span>
									</div>
								</li>
							</ul>
						</div>

						<div className="gnv3-social-visual" aria-hidden="true">
							<svg viewBox="0 0 480 460" className="gnv3-social-graph" preserveAspectRatio="xMidYMid meet">
								<defs>
									<radialGradient id="gnv3-node-grad" cx="50%" cy="50%" r="50%">
										<stop offset="0%" stopColor="#5dd2c6" />
										<stop offset="100%" stopColor="#30a49a" />
									</radialGradient>
									<linearGradient id="gnv3-edge-grad" x1="0" y1="0" x2="1" y2="1">
										<stop offset="0%" stopColor="rgba(48,164,154,0.55)" />
										<stop offset="100%" stopColor="rgba(248,123,27,0.35)" />
									</linearGradient>
								</defs>

								{/* edges (irregular) */}
								<g className="gnv3-social-edges" stroke="url(#gnv3-edge-grad)" strokeWidth="1.6" fill="none">
									<path d="M 90 110 Q 170 140 240 80" />
									<path d="M 240 80 Q 330 130 400 180" />
									<path d="M 90 110 Q 120 220 180 280" />
									<path d="M 180 280 Q 260 300 340 240" />
									<path d="M 340 240 Q 380 320 290 380" />
									<path d="M 180 280 Q 200 360 290 380" />
									<path d="M 400 180 Q 420 260 340 240" />
									<path d="M 240 80 Q 230 200 180 280" />
									<path d="M 90 110 Q 200 60 240 80" />
									<path d="M 290 380 Q 200 410 130 360" />
									<path d="M 130 360 Q 110 240 180 280" />
								</g>

								{/* nodes */}
								<g className="gnv3-social-nodes">
									<circle cx="90" cy="110" r="22" fill="url(#gnv3-node-grad)" />
									<text x="90" y="116" textAnchor="middle" className="gnv3-social-init">LM</text>

									<circle cx="240" cy="80" r="28" fill="url(#gnv3-node-grad)" />
									<text x="240" y="87" textAnchor="middle" className="gnv3-social-init gnv3-social-init-lg">JT</text>

									<circle cx="400" cy="180" r="20" fill="url(#gnv3-node-grad)" />
									<text x="400" y="186" textAnchor="middle" className="gnv3-social-init">AC</text>

									<circle cx="180" cy="280" r="26" fill="url(#gnv3-node-grad)" />
									<text x="180" y="287" textAnchor="middle" className="gnv3-social-init gnv3-social-init-lg">RD</text>

									<circle cx="340" cy="240" r="22" fill="url(#gnv3-node-grad)" />
									<text x="340" y="246" textAnchor="middle" className="gnv3-social-init">SL</text>

									<circle cx="290" cy="380" r="24" fill="url(#gnv3-node-grad)" />
									<text x="290" y="387" textAnchor="middle" className="gnv3-social-init">MV</text>

									<circle cx="130" cy="360" r="18" fill="url(#gnv3-node-grad)" />
									<text x="130" y="366" textAnchor="middle" className="gnv3-social-init">EP</text>
								</g>

								{/* floating reaction bubbles */}
								<g className="gnv3-social-reactions">
									<g transform="translate(160 50)">
										<circle r="22" fill="#fff" className="gnv3-social-bubble" />
										<text y="8" textAnchor="middle" fontSize="22">💪</text>
									</g>
									<g transform="translate(370 110)">
										<circle r="22" fill="#fff" className="gnv3-social-bubble" />
										<text y="8" textAnchor="middle" fontSize="22">❤️</text>
									</g>
									<g transform="translate(80 230)">
										<circle r="22" fill="#fff" className="gnv3-social-bubble" />
										<text y="8" textAnchor="middle" fontSize="22">😳</text>
									</g>
									<g transform="translate(420 330)">
										<circle r="22" fill="#fff" className="gnv3-social-bubble" />
										<text y="8" textAnchor="middle" fontSize="22">😂</text>
									</g>
									<g transform="translate(220 420)">
										<circle r="22" fill="#fff" className="gnv3-social-bubble" />
										<text y="8" textAnchor="middle" fontSize="22">💪</text>
									</g>
								</g>
							</svg>
						</div>
					</div>
				</section>
			</FadeInSection>

			{/* ============ PRICING ============ */}
			<FadeInSection>
				<section className="gnv3-sec" id="pricing">
					<div className="gnv3-wrap">
						<div className="gnv3-sec-head center">
							<span className="gnv3-eyebrow">Tarifs</span>
							<h2 className="gnv3-h2">Commence gratuitement.</h2>
							<p className="gnv3-sub">Passe au niveau supérieur quand tu es prêt. Sans engagement.</p>
						</div>
						<div className="gnv3-billing-toggle">
							<span className={billingPeriod === "monthly" ? "active" : ""}>
								Mensuel
							</span>
							<label className="gnv3-switch">
								<input
									type="checkbox"
									checked={billingPeriod === "yearly"}
									onChange={() =>
										setBillingPeriod(billingPeriod === "monthly" ? "yearly" : "monthly")
									}
								/>
								<span className="gnv3-slider"></span>
							</label>
							<span className={billingPeriod === "yearly" ? "active" : ""}>
								Annuel <span className="gnv3-savings">-17%</span>
							</span>
						</div>

						<div className="gnv3-price-grid">
							<div className="gnv3-price">
								<div className="gnv3-pn">Sportif</div>
								<div className="gnv3-pp">0€<span> / toujours</span></div>
								<div className="gnv3-pd">Pour démarrer et suivre tes séances.</div>
								<ul>
									<li><span className="mdi mdi-check-circle-outline"></span>Séances illimitées</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Bibliothèque d'exercices</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Minuteur de repos</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Suivi du poids de corps</li>
									<li><span className="mdi mdi-check-circle-outline"></span>Partage avec tes Gymbros</li>
								</ul>
							</div>
							<div className="gnv3-price gnv3-price-feature">
								<span className="gnv3-flag">Le plus populaire</span>
								<div className="gnv3-pn">Coach Starter</div>
								<div className="gnv3-pp">
									{starterPrice}€<span>{starterIsYearly ? " / an" : " / mois"}</span>
								</div>
								{starterIsYearly && (
									<div className="gnv3-pd">soit {(starterPrice / 12).toFixed(2)}€/mois</div>
								)}
								{!starterIsYearly && (
									<div className="gnv3-pd">Pour celles et ceux qui débutent le coaching.</div>
								)}
								<ul>
									{starterFeatures.map((f, i) => (
										<li key={i}><span className="mdi mdi-check-circle-outline"></span>{f}</li>
									))}
								</ul>
							</div>
							<div className="gnv3-price">
								<div className="gnv3-pn">Coach Pro</div>
								<div className="gnv3-pp">
									{proPrice}€<span>{proIsYearly ? " / an" : " / mois"}</span>
								</div>
								{proIsYearly && (
									<div className="gnv3-pd">soit {(proPrice / 12).toFixed(2)}€/mois</div>
								)}
								{!proIsYearly && (
									<div className="gnv3-pd">Pour les coachs qui suivent leurs athlètes à l'échelle.</div>
								)}
								<ul>
									{proFeatures.map((f, i) => (
										<li key={i}><span className="mdi mdi-check-circle-outline"></span>{f}</li>
									))}
								</ul>
							</div>
						</div>
						<p className="gnv3-price-note">
							<span className="mdi mdi-apple"></span>
							<span className="mdi mdi-google-play"></span>
							Les abonnements se souscrivent directement dans l'app, sur l'App Store ou Google Play.
						</p>
					</div>
				</section>
			</FadeInSection>

			{/* ============ FAQ ============ */}
			<FaqAccordion />

			{/* ============ FINAL CTA ============ */}
			<FadeInSection>
				<section className="gnv3-sec gnv3-sec-band" id="download">
					<div className="gnv3-cta-band">
						<div className="gnv3-band-glow"></div>
						<h2 className="gnv3-h2 gnv3-h2-on-dark">Tes élèves méritent mieux qu'un fichier Excel.</h2>
						<p className="gnv3-sub gnv3-sub-on-dark">
							Télécharge l'app, active le mode coach et invite ton premier
							élève en quelques minutes.
						</p>
						<div className="gnv3-cta-actions">
							<a
								href="https://apps.apple.com/fr/app/gymnote-suivi-muscu-coach/id6470710692"
								target="_blank"
								rel="noopener noreferrer"
								className="gnv3-store-link"
							>
								<img src={appstore} alt="Télécharger dans l'App Store" />
							</a>
							<a
								href="https://play.google.com/store/apps/details?id=com.frenchmatrix.GymNote"
								target="_blank"
								rel="noopener noreferrer"
								className="gnv3-store-link"
							>
								<img src={playstore} alt="Disponible sur Google Play" />
							</a>
						</div>
					</div>
				</section>
			</FadeInSection>
		</div>
	);
}

export default Home;
